import React, { useState, useEffect } from 'react';
import FooterHome from '../Layouts/FooterHome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift, faRightToBracket } from '@fortawesome/free-solid-svg-icons';

const HUBSPOT_FORM_URL =
  'https://tdgrm.share.hsforms.com/2o1NiMr-WRiWEniTAEpv4KQ';

const translations = {
  es: {
    headerSubtitle:
      '¿Quieres unirte a nuestra app?<br />Por ahora es solo con invitación, pero puedes entrar en la lista de espera aquí.<br />¡Anímate!',
    cta: 'Regístrate',
  },
  en: {
    headerSubtitle:
      'Would you like to join our app?<br />For now, it is by invitation only, but you can join the waiting list here.<br />Give it a try!',
    cta: 'Register',
  },
};

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  mainSection: {
    padding: '50px 20px',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  formContainer: {
    display: 'flex',
    maxWidth: '800px',
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    margin: '0 auto',
  },
  leftColumn: {
    backgroundColor: '#FF2300',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
    boxSizing: 'border-box',
  },
  logo: {
    maxWidth: '100%',
    height: 'auto',
  },
  rightColumn: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: '40px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoText: {
    fontWeight: '700',
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'center',
    marginBottom: '20px',
    color: '#333',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '200px',
    padding: '12px',
    backgroundColor: '#ff2300',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    boxSizing: 'border-box',
    marginTop: '20px',
  },
  buttonIcon: {
    marginRight: '8px',
    fontSize: '18px',
  },
};

// 👉 función segura para leer/crear el idioma
function getInitialLanguage() {
  if (typeof window === 'undefined') return 'es';
  const saved = localStorage.getItem('language');
  if (saved === 'es' || saved === 'en') return saved;
  // Si no hay valor o es inválido, forzamos ES y lo persistimos
  localStorage.setItem('language', 'es');
  return 'es';
}

function RegistrationPage() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const t = translations[language];

  // Persistimos SOLO el valor válido ('es' | 'en')
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const safe = language === 'en' ? 'en' : 'es';
      localStorage.setItem('language', safe);
    }
  }, [language]);

  // Al pulsar el botón, abre el formulario de HubSpot en nueva pestaña
  const handleCustomClick = () => {
    if (typeof window !== 'undefined') {
      window.open(HUBSPOT_FORM_URL, '_blank', 'noopener,noreferrer');
    }
  };

  // Ajuste de layout para móvil
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={styles.container}>
      {/* Reset global */}
      <style>{`
        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }
        *, *::before, *::after {
          box-sizing: border-box;
        }
      `}</style>

      <main style={styles.mainSection}>
        <div
          style={{
            ...styles.formContainer,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          {/* Columna izquierda: logo */}
          <div
            style={{
              ...styles.leftColumn,
              padding: isMobile ? '20px' : styles.leftColumn.padding,
              marginBottom: isMobile ? '20px' : undefined,
            }}
          >
            <a
              href="/"
              style={
                isMobile
                  ? { display: 'block', width: '100%', textAlign: 'center' }
                  : { display: 'inline-block' }
              }
            >
              <img
                src="/BetherLogowhite.png"
                alt="Company logo"
                style={{
                  ...styles.logo,
                  maxWidth: isMobile ? '100%' : styles.logo.maxWidth,
                }}
              />
            </a>
          </div>

          {/* Columna derecha: icono + texto + botón que abre HubSpot */}
          <div
            style={{
              ...styles.rightColumn,
              padding: isMobile ? '20px' : styles.rightColumn.padding,
            }}
          >
            <FontAwesomeIcon
              icon={faGift}
              style={{
                fontSize: isMobile ? '30px' : '40px',
                marginBottom: '20px',
                color: '#ff2300',
              }}
            />

            <p
              style={{
                ...styles.infoText,
                fontSize: isMobile ? '16px' : styles.infoText.fontSize,
              }}
              dangerouslySetInnerHTML={{ __html: t.headerSubtitle }}
            />

            {/* Único botón visible: abre el formulario de HubSpot */}
            <button
              type="button"
              style={styles.button}
              onClick={handleCustomClick}
            >
              <FontAwesomeIcon
                icon={faRightToBracket}
                style={styles.buttonIcon}
              />
              {t.cta}
            </button>
          </div>
        </div>
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default RegistrationPage;
